import {
  decodeIceServerMessage,
  encodeMessage,
  ICE_PROTOCOL_VERSION,
  type IceClientMessage,
  type IceServerMessage,
} from '@halvard/protocol';
import { ice } from '@halvard/rules';

/** The part of a browser WebSocket the connection uses. */
export interface SocketLike {
  send(text: string): void;
  close(): void;
  onopen: (() => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onclose: (() => void) | null;
}

export type ConnectionStatus = 'connecting' | 'online' | 'offline' | 'outdated';

export interface IceSocketOptions {
  url: string;
  /** Where the guest token is kept between visits. */
  tokens: { get(): string | null; set(token: string): void };
  onMessage: (message: IceServerMessage) => void;
  onStatus: (status: ConnectionStatus) => void;
  /** Called after each `welcome`, first connection and reconnections alike. */
  onReady: () => void;
  open?: (url: string) => SocketLike;
  schedule?: (run: () => void, ms: number) => void;
}

export interface IceSocket {
  send(message: IceClientMessage): void;
  close(): void;
}

/** Pauses before reconnecting: 1 s, 2 s, 4 s… up to 10 s. */
export function retryDelay(attempt: number): number {
  return Math.min(10_000, 1000 * 2 ** attempt);
}

/** The game server's `/ice` address: `VITE_ICE_SERVER`, or port 8787 on the page's own host. */
export function iceServerUrl(
  location: { protocol: string; hostname: string },
  configured?: string,
) {
  if (configured) return configured;
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.hostname}:8787/ice`;
}

/**
 * A connection to `/ice` that says hello with the stored guest token, keeps the token it is
 * given, and reconnects after a drop (the caller rejoins its match in `onReady`). Messages that
 * don't pass the shared schemas are dropped.
 */
export function openIceSocket(options: IceSocketOptions): IceSocket {
  const open = options.open ?? ((url) => new WebSocket(url) as unknown as SocketLike);
  const schedule = options.schedule ?? ((run, ms) => void setTimeout(run, ms));
  let socket: SocketLike | null = null;
  let ready = false;
  let closed = false;
  let attempt = 0;
  const queue: IceClientMessage[] = [];

  const connect = () => {
    if (closed) return;
    options.onStatus('connecting');
    const s = open(options.url);
    socket = s;
    s.onopen = () => {
      const token = options.tokens.get();
      s.send(
        encodeMessage({
          type: 'hello',
          protocolVersion: ICE_PROTOCOL_VERSION,
          rulesVersion: ice.ICE_RULES_VERSION,
          ...(token ? { token } : {}),
        }),
      );
    };
    s.onmessage = (event) => {
      if (typeof event.data !== 'string') return;
      const decoded = decodeIceServerMessage(event.data);
      if (!decoded.ok) return;
      const message = decoded.message;
      if (message.type === 'welcome') {
        options.tokens.set(message.guestToken);
        ready = true;
        attempt = 0;
        options.onStatus('online');
        options.onReady();
        for (const queued of queue.splice(0)) s.send(encodeMessage(queued));
        return;
      }
      if (message.type === 'error' && message.code === 'CLIENT_OUTDATED') {
        closed = true;
        options.onStatus('outdated');
      }
      options.onMessage(message);
    };
    s.onclose = () => {
      ready = false;
      if (socket !== s || closed) return;
      options.onStatus('offline');
      schedule(connect, retryDelay(attempt++));
    };
  };
  connect();

  return {
    send: (message) => {
      if (ready && socket) socket.send(encodeMessage(message));
      else queue.push(message);
    },
    close: () => {
      closed = true;
      socket?.close();
    },
  };
}
